import React from "react"
import { useAlert } from "react-alert"
import { observer } from "mobx-react-lite"

import { useController, useStore } from "@store/index"
import { ModalsTypes } from "@components/modals/constants"
import useModal from "@components/modals/useModal"
import Rating from "@components/ui/Rating/Rating"
import Text from "@components/ui/Typography/Text"
import Icon from "@components/ui/Icon/Icon"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/ui/Tooltip/v2/Tooltip"

import styles from "./StarRatings.module.sass"

const SUCCESS_MESSAGE_TITLE = "You've successfully voted"
const SUCCESS_MESSAGE_SUBTITLE = "Thank you for your feedback!"

type Props = {
  reportId: string
}

const WorkbookReportFeedbackWidget: React.FC<Props> = observer(
  ({ reportId }) => {
    const alert = useAlert()
    const modal = useModal(ModalsTypes.ANSWER_FEEDBACK_MODAL)

    const { workbookReportsStore, authStore } = useStore()
    const { factFinderSolutionController } = useController()

    const reportData = workbookReportsStore.getById(reportId)
    const feedbackStore = reportData.feedback

    const handleFeedback = async (ratingVal: number) => {
      modal.showModal({
        onSubmit: async (rating: number, message: string) => {
          const error =
            await factFinderSolutionController.voteForWorkbookReport(
              reportId,
              rating,
              message
            )

          if (error != null) {
            alert.error(error)
            return
          }

          alert.success(
            <>
              {SUCCESS_MESSAGE_TITLE}
              <Text variant="h5" color="text50Color">
                {SUCCESS_MESSAGE_SUBTITLE}
              </Text>
            </>
          )

          modal.hideModal()
        },
        rating: ratingVal,
      })
    }

    const disabled = !(
      feedbackStore.isEditable &&
      authStore.isCurrentUser(reportData.createdById ?? null)
    )

    return (
      <Tooltip placement="top-start" color="primary">
        <TooltipTrigger>
          <Rating
            value={feedbackStore.rating}
            disabled={disabled}
            onChange={handleFeedback}
          />
        </TooltipTrigger>
        <TooltipContent maxWidth="none" className={styles.tooltip}>
          <div className={styles.defaultRating}>
            <Text variant="h4">
              {disabled
                ? `You rated ${feedbackStore.rating}`
                : "Default rating 3"}
            </Text>
            <Icon name="star-filled" color="gold" />
          </div>

          {feedbackStore.comment ? (
            <Text variant="h6">{feedbackStore.comment}</Text>
          ) : (
            !disabled && (
              <Text variant="h6">
                You can also write a feedback by clicking on the star ratings
              </Text>
            )
          )}
        </TooltipContent>
      </Tooltip>
    )
  }
)

export default WorkbookReportFeedbackWidget
